import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";
import {Grid, Typography} from "@mui/material";
import {FormAutocomplete, FormDateTimePicker, FormTextField,} from "../../../common/form-fields";
import {FormReportModel} from "../../../api/types/report";
import {datesAtom, monthsAtom} from "../../../state/reportRecoil";
import {useRecoilState} from "recoil";
import {SelectElementModelBE, SelectElementModelFE,} from "../../../api/types/generic";
import {useLocationGroupRepository} from "../../../api/repositories/LocationGroupRepository";
import React, {useContext, useEffect, useState} from "react";
import {fromGenericBeToGenericFe, setFormNumberValue} from "../../../utils";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {numberIntegerRegex} from "../../../constants";
import DealerAndTenant, {DealerTenantEntity} from "../../../common/components/DealerAndTenant";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import dayjs from "dayjs";

const RevenuesForm = ({readOnly}: { readOnly: boolean }) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    //USE FORM CONTEXT
    const {register, watch, setValue} = useFormContext<FormReportModel>();

    const watchAllForm = watch();

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //USE RECOIL ATOM
    const [monthsList] = useRecoilState(monthsAtom);
    const [dates] = useRecoilState(datesAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState<
        SelectElementModelFE[]
    >(commercialSegmentListAtom);

    //USE STATE
    const [locationGroups, setLocationGroups] = useState<SelectElementModelFE[]>(
        []
    );
    const [dealerOptions, setDealerOptions] = useState([])
    const [yearList] = useState([dayjs().year() - 2, dayjs().year() - 1 ,dayjs().year(), dayjs().year() + 1, dayjs().year() + 2])

    //USE HOOK CUSTOM
    const {getLocationGroups} = useLocationGroupRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();

    const onChangeLocationGroup = (input: SelectElementModelFE[]) => {
        const value: SelectElementModelFE[] =
            // è stato cliccato "selectAll
            input.find((el: SelectElementModelFE) => el.code === 0) &&
            // tra i vecchi valori non c'è "selectAll
            !watchAllForm.revenues.locationsGroupIds?.find(
                (el: SelectElementModelFE) => el.code === 0
            )
                ? // applica il "selectAll
                locationGroups.filter((el: SelectElementModelFE) => el.code !== 0)
                : // setta solo i car group selezionati
                input.filter((el) => el.code !== 0);
        setValue("revenues.locationsGroupIds", value);
    };

    const handleGetStaticContent = async () => {
        // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
        // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] =
            fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    useEffect(() => {
        handleGetStaticContent();
      if (optionsFiltered.length === 1) {
        updateDealerSelection(optionsFiltered[0]);
      } else {
        let legalEntity = optionsFiltered.find((el) => el.code === watch("legalEntity")?.code);
        let dealer = dealerOption.find((el) => el.code === watch("dealer")?.code);
        if (legalEntity) {
          updateDealerSelection(legalEntity);
        }
        if (dealer) {
          setValue("dealer", dealer);
        }
      }
    }, []);

    const updateDealerSelection = async (value: SelectElementModelFE) => {
        setValue(`legalEntity`, value);
        setValue(`dealer`, null);
        const filteredOptionsDealer = user.dealers?.filter(
            (el: DealerTenantEntity) => el.legalEntities?.some((el: { name: string | number; }) => el.name === value?.code)
        );
        const filteredList = filteredOptionsDealer.map(
            (el: DealerTenantEntity) => ({
                code: el.name,
                label: t('translation:dealers.'.concat(el.name)),
            })
        );
        await setDealerOptions(filteredList);
        if (filteredList.length === 1) {
            setValue(`dealer`, filteredList[0]);
        }

        const locationGroupsData: SelectElementModelBE[] =
            await getLocationGroups(value?.code as string);
        const locationsGroups: SelectElementModelFE[] =
            fromGenericBeToGenericFe(locationGroupsData);
        setLocationGroups([
            {code: 0, label: t("pricing:actions.selectAll")},
            ...locationsGroups,
        ]);
    };

  const renderDealerAndTenant = () => {
    return (
      <>
        <Typography variant="h6" width={"100%"} pl={2} mt={4}
                    hidden={optionsFiltered.length <= 1 || dealerFilteredOptions.length <= 1}>
          {t("pricing:alert.labels.dealerAndTenant")}
        </Typography>
        <Typography variant="h6" width={"100%"} pl={2} mt={4} hidden={optionsFiltered.length <= 1 || dealerFilteredOptions.length > 1}>
          {t("pricing:alert.labels.tenant")}
        </Typography>
        <Typography variant="h6" width={"100%"} pl={2} mt={4} hidden={dealerFilteredOptions.length <= 1 || optionsFiltered.length > 1 }>
          {t("pricing:alert.labels.dealer")}
        </Typography>
        <Grid item xs={12} md={3} hidden={optionsFiltered.length <= 1}>
          <FormAutocomplete
            {...register("legalEntity")}
            label={`${t("pricing:dealer.tenant")}*`}
            //getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.label))}
            options={optionsFiltered || []}
            handleChange={updateDealerSelection}
            isOptionEqualToValue={(
              option: SelectElementModelFE,
              value: SelectElementModelFE
            ) => option.code === value.code}
          />
        </Grid>
        <Grid item xs={12} md={3} hidden={dealerFilteredOptions.length <= 1}>
          <FormAutocomplete
            {...register(`dealer`)}
            label={`${t("pricing:dealer.dealer")}*`}
            options={dealerOptions || []}
            readOnly={Boolean(!watch("legalEntity"))}
            //getOptionLabel={(option: any) => t('translation:dealers.'.concat(option.label))}
            isOptionEqualToValue={(
              option: SelectElementModelFE,
              value: SelectElementModelFE
            ) => option.code === value.code}
          />
        </Grid>
      </>
    );
  };

    return (
        <Grid container columnSpacing={2}>
            {/* DEALER/TENANT */}
          {renderDealerAndTenant()}
            <Typography variant="h6" width={"100%"} pl={2} mt={4}>
                {t("pricing:report.labels.searchParameters")}
            </Typography>
            {/* MONTH PICK UP */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("revenues.startMonthPickUp")}
                    label={`${t("pricing:report.form.startMonth")}*`}
                    options={monthsList}
                    isOptionEqualToValue={(option: string, value: string) =>
                        option === value
                    }
                />
            </Grid>
            {/* YEAR PICK UP */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("revenues.year")}
                    label={`${t("pricing:report.form.year")}*`}
                    options={yearList}
                    isOptionEqualToValue={(option: number, value: number) =>
                        option === value
                    }
                />
            </Grid>
            {/* M */}
            <Grid item xs={12} md={3}>
                <FormTextField
                    {...register("revenues.numberOfMonths")}
                    label={`${t("pricing:report.form.numberOfMonths")}*`}
                    type={"text"}
                    handleChange={(value: string) =>
                        setFormNumberValue(
                            value,
                            setValue,
                            "revenues.numberOfMonths",
                            numberIntegerRegex
                        )
                    }
                />
            </Grid>
            {/* DATE TYPE */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("revenues.dateType")}
                    label={`${t("pricing:report.form.dateType")}*`}
                    options={dates}
                    isOptionEqualToValue={(
                        option: SelectElementModelFE,
                        value: SelectElementModelFE
                    ) => option.code === value.code}
                />
            </Grid>
            {/* GROUPS OF LOCATIONS */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    multiple={true}
                    {...register("revenues.locationsGroupIds")}
                    label={`${t("pricing:report.labels.groupsOfLocations")}*`}
                    options={locationGroups}
                    handleChange={onChangeLocationGroup}
                    isOptionEqualToValue={(
                        option: SelectElementModelFE,
                        value: SelectElementModelFE
                    ) => option.code === value.code}
                />
            </Grid>
            {/* PICK UP FROM */}
            <Grid item xs={12} md={3}>
                <FormDateTimePicker
                    {...register("revenues.startDate")}
                    label={`${t("pricing:report.numberOfDaysOfReservations.startDate")}*`}
                    maxDate={watch("revenues.endDate") || undefined}
                />
            </Grid>
            {/* PICK UP TO */}
            <Grid item xs={12} md={3}>
                <FormDateTimePicker
                    {...register("revenues.endDate")}
                    label={`${t("pricing:report.numberOfDaysOfReservations.endDate")}`}
                    minDate={watch("revenues.startDate") || undefined}
                    readOnly={!watch("revenues.startDate")}
                />
            </Grid>
            {/* COMMERCIAL SEGMENT */}
            <Grid item xs={12} md={3}>
                <FormAutocomplete
                    {...register("revenues.smCommercialSegmentId")}
                    label={`${t("pricing:report.form.smCommercialSegment")}`}
                    options={commercialSegmentList}
                    isOptionEqualToValue={(
                        option: SelectElementModelFE,
                        value: SelectElementModelFE
                    ) => option.code === value.code}
                />
            </Grid>
        </Grid>
    );
};

export default RevenuesForm;
